/**
 * Integration Card Component
 * Displays a single integration configuration with actions
 */

import React, { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Play,
  Pause,
  Settings,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  MoreVertical,
  Edit,
  TestTube,
  Calendar,
  TrendingUp
} from 'lucide-react';
import {
  IntegrationConfig,
  getIntegrationTypeLabel,
  updateIntegration,
  deleteIntegration,
  testConnection,
  executeIntegration
} from '@/services/api/integrations';

interface IntegrationCardProps {
  integration: IntegrationConfig;
  onEdit: (integration: IntegrationConfig) => void;
  onExecute: (integration: IntegrationConfig) => void;
  onRefresh: () => void;
}

export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onEdit,
  onExecute,
  onRefresh
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [showMenu, setShowMenu] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [executing, setExecuting] = useState(false);

  const handleToggleActive = async () => {
    try {
      await updateIntegration(integration.id!, { is_active: !integration.is_active });
      onRefresh();
    } catch (err) {
      console.error('Error toggling integration:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${integration.name}"?`)) {
      return;
    }

    try {
      await deleteIntegration(integration.id!);
      onRefresh();
    } catch (err) {
      console.error('Error deleting integration:', err);
      alert('Failed to delete integration');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await testConnection(integration.id!);
      setTestResult(result);

      // Clear result after 5 seconds
      setTimeout(() => setTestResult(null), 5000);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      await executeIntegration(integration.id!, { execution_mode: 'manual' });
      onExecute(integration);
    } catch (err) {
      console.error('Error executing integration:', err);
      alert('Failed to start execution');
    } finally {
      setExecuting(false);
    }
  };

  const typeColors: Record<string, string> = {
    health_plan: 'bg-purple-100 text-purple-700 border-purple-200',
    fhir: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    crm: 'bg-pink-100 text-pink-700 border-pink-200',
    housing: 'bg-teal-100 text-teal-700 border-teal-200',
    benefit_systems: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  const formatDate = (date?: string) => {
    if (!date) return 'Never';
    return fmt.dateTime(date);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                {integration.name}
              </h3>
              {integration.is_active ? (
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
            </div>

            <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${typeColors[integration.integration_type]}`}>
              {getIntegrationTypeLabel(integration.integration_type)}
            </span>
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <MoreVertical className="h-5 w-5 text-gray-500" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-8 z-20 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 py-1 w-48">
                  <button
                    onClick={() => {
                      onEdit(integration);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    {t('integrations.edit_configuration', 'Edit Configuration')}
                  </button>
                  <button
                    onClick={() => {
                      handleTestConnection();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <TestTube className="h-4 w-4" />
                    {t('integrations.test_connection', 'Test Connection')}
                  </button>
                  <button
                    onClick={() => {
                      handleToggleActive();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    {integration.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    {integration.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      handleDelete();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('integrations.delete', 'Delete')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {integration.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {integration.description}
          </p>
        )}

        {/* Connection Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg mb-3 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-2">
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.message}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('integrations.last_run', 'Last Run')}</p>
              <p className="text-gray-900 font-medium">
                {formatDate(integration.last_execution_at)}
              </p>
            </div>
          </div>

          {integration.schedule_config?.enabled && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('integrations.schedule', 'Schedule')}</p>
                <p className="text-gray-900 font-medium capitalize">
                  {integration.schedule_config.frequency}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 flex gap-2">
        <button
          onClick={handleExecute}
          disabled={!integration.is_active || executing}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {executing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              {t('integrations.executing', 'Executing...')}
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              {t('integrations.execute_now', 'Execute Now')}
            </>
          )}
        </button>

        <button
          onClick={handleTestConnection}
          disabled={testing}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {testing ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
          ) : (
            <TestTube className="h-4 w-4" />
          )}
        </button>

        <button
          onClick={() => onEdit(integration)}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
